import React, { useReducer, Dispatch } from 'react'
import { initialAppState } from './store'
import { Props } from '../modules/types'
import rootReducer, { Action, ErrorAction } from './rootReducer'
import { AppState } from './store'
import { LOG_ERROR } from './diags/store/reducers'
import { v4 } from 'uuid'
import { HttpError, ActionError } from './errors'
import * as SentryClient from '@sentry/react'
import * as diagsActions from './diags/store/actions'
import { ShowConfirmParams } from './diags/store/actions'

export type ExecuteFunc = (state: AppState, dispatch: Dispatch<Action>) => Promise<void> | void

export type AppContextType = {
  state: AppState
  execute: (action: ExecuteFunc) => Promise<void>
  confirm: (params: ShowConfirmParams) => void
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const AppContext = React.createContext({} as any as AppContextType)

function handleOriginalError(dispatch: Dispatch<Action>, error: Error) {
  if (error instanceof HttpError) {
    dispatch({
      type: LOG_ERROR,
      errorInfo: {
        id: v4(),
        httpStatus: error.httpStatus,
        errorCode: error.errorCode,
        fromApi: true
      }
    })

    // unauthorized and offline
    if (error.httpStatus !== 408 && error.httpStatus !== 401) {
      if (SentryClient && SentryClient.captureException) {
        SentryClient.captureException(error, {
          contexts: {
            'Request': error.request,
            'Response': { httpStatus: error.httpStatus, errorCode: error.errorCode, message: error.message }
          }
        })
      }
    }
  } else {
    if (SentryClient && SentryClient.captureException) {
      SentryClient.captureException(error)
    }

    dispatch({
      type: LOG_ERROR,
      errorInfo: {
        id: v4(),
        fromApi: false,
        error
      }
    })
  }
}

export function handleError(dispatch: Dispatch<Action>, error: Error) {
  if (error instanceof ActionError) {
    dispatch({
      type: error.action as ErrorAction
    })

    handleOriginalError(dispatch, error.originalError)
  } else {
    handleOriginalError(dispatch, error)
  }
}

function AppContextProvider(props: Props) {
  const [state, dispatch] = useReducer(rootReducer, initialAppState)

  async function execute(action: (state: AppState, dispatch: Dispatch<Action>) => Promise<void> | void) {
    try {
      await action(state, dispatch)
    } catch (error) {
      handleError(dispatch, error)
    }
  }

  function confirm(params: ShowConfirmParams) {
    execute(diagsActions.showConfirm(params))
  }

  return (
    <AppContext.Provider value={{ state, execute, confirm }}>
      {props.children}
    </AppContext.Provider>
  )
}

export default AppContextProvider
